import { memo, useCallback, useMemo } from 'react';
import type { FC } from 'react';

import classNames from 'classnames';
import { useTranslation } from 'react-i18next';
import type { AssetSliderComponent } from '@vgtv/api-client/lib/component/slider';
import { Calendar as CalendarIcon } from '@vgno/icons';

import { Slider } from '../../../Slider/Slider';
import { useComponentDataFetch } from '../../useComponentDataFetch';
import { fetchSvpAssets } from '../../../../modules/assets';
import {
  Placeholder,
  placeholderTypes,
} from '../../../Placeholder/Placeholder';
import { usePlaceholders } from '../../usePlaceholders';
import { useAssetsScheduleProps } from '../../../AssetsSchedule/useAssetsScheduleProps';
import { useComponentAssets } from '../../../utils/useComponentAssets';
import { AssetItem } from '../../common/AssetItem/AssetItem';
import { useAssetItemTracking } from '../../common/useAssetItemTracking';
import { useViewportType } from '../../../utils/useViewportType';
import { useComponentPopup } from '../../ComponentPopup/useComponentPopup';

import * as styles from './AssetsSlider.module.scss';

interface AssetsSliderProps {
  componentConfig: AssetSliderComponent;
  pageComponentIndex: number;
}

const AssetsSliderBase: FC<AssetsSliderProps> = ({
  componentConfig,
  pageComponentIndex,
}) => {
  const { isFetched, visibleItemsCount } = useComponentDataFetch({
    componentConfig,
    fetchAction: fetchSvpAssets,
  });

  const { isLiveScheduleSupported } = useAssetsScheduleProps(componentConfig);
  const { t } = useTranslation();

  const headerButtonLabel = t('see all');

  const { trackAssetClick, trackHeaderButtonClick } = useAssetItemTracking({
    elementType: 'AssetsSlider',
    header: componentConfig.header,
    headerButtonLabel,
    pageComponentIndex,
  });

  const { setComponentPopupId } = useComponentPopup();

  const { isDesktop } = useViewportType();

  const { id, header, options } = componentConfig;
  const { useLargeThumbnails, skipMetadata } = componentConfig.options;

  const displayLargeThumbnails = useLargeThumbnails && isDesktop;

  const assets = useComponentAssets(id);

  const verticalThumbnail = options.useVerticalPosters;

  const placeholders = usePlaceholders(isFetched);

  const items = useMemo(() => {
    return [
      ...placeholders.map((item, index) => (
        <Placeholder
          key={index}
          type={
            verticalThumbnail
              ? placeholderTypes.assetWithVerticalThumbnail
              : placeholderTypes.assetVertical
          }
        />
      )),
      ...assets.map((asset, index) => {
        return (
          <AssetItem
            key={asset.id}
            asset={asset}
            componentConfig={componentConfig}
            aspectRatio={verticalThumbnail ? 'vertical' : 'horizontal'}
            onClick={() => trackAssetClick(asset, index)}
            skipMetadata={skipMetadata}
            size={displayLargeThumbnails ? 'large' : 'medium'}
          />
        );
      }),
    ];
  }, [
    assets,
    componentConfig,
    placeholders,
    verticalThumbnail,
    trackAssetClick,
    skipMetadata,
    displayLargeThumbnails,
  ]);

  const onHeaderButtonClick = useCallback(() => {
    trackHeaderButtonClick();

    setComponentPopupId(componentConfig.id);
  }, [trackHeaderButtonClick, setComponentPopupId, componentConfig]);

  if (isFetched && assets.length === 0) {
    return null;
  }

  const showHeaderButton =
    isLiveScheduleSupported ||
    (assets.length > Math.ceil(visibleItemsCount as number) &&
      Boolean(componentConfig.data.url));

  const headerButton = isLiveScheduleSupported ? (
    <>
      <CalendarIcon />
      {headerButtonLabel}
    </>
  ) : (
    headerButtonLabel
  );

  return (
    <Slider
      header={header}
      headerButtonLabel={showHeaderButton ? headerButton : undefined}
      onHeaderButtonClick={showHeaderButton ? onHeaderButtonClick : undefined}
      className={classNames(styles.slider, {
        [styles.vertical]: verticalThumbnail,
        [styles.large]: displayLargeThumbnails && !verticalThumbnail,
      })}
    >
      {items}
    </Slider>
  );
};

export const AssetsSlider = memo(AssetsSliderBase);
